import React, { createContext, useRef, useContext } from "react";
import { Toast } from "primereact/toast";

const ToastContext = createContext();

export const useToast = () => useContext(ToastContext);

let globalToast = null; // Global toast reference
let suppressedErrors = new WeakSet(); // Track globally handled errors
let activeToasts = {}; // Track active toasts to suppress duplicates

/**
 * Suppresses duplicate toasts for the same type and configuration.
 */
const suppressToast = (type, config, position) => {
    const toastKey = `${type}-${position}-${config.summary || config.detail}`; // Unique identifier for the toast
    if (activeToasts[toastKey]) {
        return true; // Suppress duplicate toasts
    }

    activeToasts[toastKey] = true; // Mark toast as active
    setTimeout(() => {
        delete activeToasts[toastKey]; // Remove active flag after toast duration
    }, config.life || 3000);

    return false; // Allow the toast to be displayed
};

/**
 * Shows a global toast notification.
 */
export const showGlobalToast = (type, config = {}, position = "topRight") => {
    if (globalToast && globalToast[position]?.current) {
        if (suppressToast(type, config, position)) return; // Suppress duplicate toasts

        globalToast[position].current.show({
            severity: type,
            ...config,
        });
    }
};

/**
 * Marks an error as globally handled to suppress further handling.
 */
export const markErrorAsHandled = (error) => {
    suppressedErrors.add(error);
};

/**
 * Checks if an error has been globally handled.
 */
export const isErrorHandledGlobally = (error) => suppressedErrors.has(error);

export const ToastProvider = ({ children }) => {
    const toastRefs = {
        topCenter: useRef(null),
        topLeft: useRef(null),
        topRight: useRef(null),
        center: useRef(null),
        bottomLeft: useRef(null),
        bottomCenter: useRef(null),
        bottomRight: useRef(null),
    };

    globalToast = toastRefs; // Set the global toast reference

    const defaultSuccessConfig = {
        severity: "success",
        summary: "Confirmed",
        detail: "Successful",
        life: 3000,
    };

    const defaultErrorConfig = {
        severity: "error",
        summary: "Error!",
        detail: "Error",
        life: 3000,
    };

    const defaultWarningConfig = {
        severity: "warn",
        summary: "Warning!",
        detail: "warning.",
        life: 3000,
    };

    const toast = {
        success: (config = {}, type = "topRight") => {
            if (suppressToast("success", config, type)) return; // Suppress duplicate toasts
            toastRefs[type]?.current?.show({
                ...defaultSuccessConfig,
                ...config,
            });
        },
        error: (config = {}, type = "topRight", error = null) => {
            if (error && isErrorHandledGlobally(error)) {
                // Suppress the toast if the error is globally handled
                return;
            }
            if (suppressToast("error", config, type)) return; // Suppress duplicate toasts
            toastRefs[type]?.current?.show({
                ...defaultErrorConfig,
                ...config,
            });
        },
        warning: (config = {}, type = "topRight") => {
            if (suppressToast("warning", config, type)) return; // Suppress duplicate toasts
            toastRefs[type]?.current?.show({
                ...defaultWarningConfig,
                ...config,
            });
        },
    };

    return (
        <ToastContext.Provider value={{ toast }}>
            {children}
            <Toast ref={toastRefs.topLeft} position="top-left" />
            <Toast ref={toastRefs.topCenter} position="top-center" />
            <Toast ref={toastRefs.topRight} position="top-right" />
            <Toast ref={toastRefs.center} position="center" />
            <Toast ref={toastRefs.bottomLeft} position="bottom-left" />
            <Toast ref={toastRefs.bottomCenter} position="bottom-center" />
            <Toast ref={toastRefs.bottomRight} position="bottom-right" />
        </ToastContext.Provider>
    );
};
